import { createNavigationContainerRef } from '@react-navigation/native';
import * as Notifications from 'expo-notifications';

import { RootStackParamList } from '@/types/navigation';

/**
 * Shared navigation handle so notification taps can drive navigation from
 * outside the React tree (listeners fire before/independently of any screen).
 * Attached to the root `NavigationContainer` in `AppNavigator`.
 */
export const navigationRef = createNavigationContainerRef<RootStackParamList>();

/** A resolved deep-link target inside the Schedule stack. */
export interface TimeOffRoute {
  screen: 'TimeOffRespond' | 'TimeOffDetail' | 'TimeOffAssemble' | 'Approvals';
  /** Absent for Approvals (the queue, not one request). */
  params?: { requestId: number; eventId?: number };
}

/** Phase 3 request events that open the request itself (SUB_UNASSIGNED shows "no longer active"). */
const DETAIL_TYPES = new Set([
  'TIMEOFF_REASSIGN',
  'TIMEOFF_APPROVED',
  'TIMEOFF_DENIED',
  'TIMEOFF_CANCELLED',
  'TIMEOFF_SUB_UNASSIGNED',
  'TIMEOFF_REMINDER', // Phase 4: requester — classes still need confirmed subs
  'TIMEOFF_ESCALATION', // Phase 4: managers — coverage at risk
]);

/**
 * Cold-start / pre-auth stash. A notification can open the app before the user
 * is authenticated (or before navigation is mounted); we hold the target and
 * replay it once both are ready.
 */
let pendingRoute: TimeOffRoute | null = null;

/**
 * Map a notification `data` payload to a route. Returns null for anything we
 * don't own (other notification types, malformed payloads) so unrelated pushes
 * are ignored, not mis-routed.
 */
export const routeForNotification = (data: unknown): TimeOffRoute | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const d = data as Record<string, unknown>;
  const requestId = Number(d.requestId);
  if (!Number.isFinite(requestId)) {
    return null;
  }

  switch (d.type) {
    case 'TIMEOFF_SUB': {
      const eventId = Number(d.eventId);
      return {
        screen: 'TimeOffRespond',
        params: { requestId, eventId: Number.isFinite(eventId) ? eventId : undefined },
      };
    }
    case 'TIMEOFF_SUBMITTED':
      // Managers: a new request to review — open the approval queue.
      return { screen: 'Approvals' };
    case 'TIMEOFF_PICK_CLASSES':
      // Schedule now published: open the class checklist (step 1) for this request.
      return { screen: 'TimeOffAssemble', params: { requestId } };
    default:
      return typeof d.type === 'string' && DETAIL_TYPES.has(d.type)
        ? { screen: 'TimeOffDetail', params: { requestId } }
        : null;
  }
};

const performNavigate = (route: TimeOffRoute): boolean => {
  if (!navigationRef.isReady()) {
    return false;
  }
  // Nested navigate: Root → Main tab → Schedule stack → target screen.
  navigationRef.navigate('Main', {
    screen: 'Schedule',
    params: { screen: route.screen, params: route.params },
  } as never);
  return true;
};

/**
 * Handle an incoming notification response. When authenticated and navigation
 * is ready we route immediately; otherwise we stash for `replayPendingRoute`.
 */
export const dispatchNotificationRoute = (data: unknown, isAuthenticated: boolean): void => {
  const route = routeForNotification(data);
  if (!route) {
    return;
  }
  if (isAuthenticated && performNavigate(route)) {
    pendingRoute = null;
  } else {
    pendingRoute = route;
  }
};

/**
 * Replay a stashed route once the user is authenticated and navigation is
 * mounted. Safe to call repeatedly (e.g. on every auth-state change); it
 * no-ops until both conditions hold, then clears the stash.
 */
export const replayPendingRoute = (isAuthenticated: boolean): void => {
  if (!isAuthenticated || !pendingRoute) {
    return;
  }
  if (performNavigate(pendingRoute)) {
    pendingRoute = null;
  }
};

/** Test/inspection helpers. */
export const getPendingRoute = (): TimeOffRoute | null => pendingRoute;
export const clearPendingRoute = (): void => {
  pendingRoute = null;
};

/**
 * Wire the notification-response listeners. Covers the running/background tap
 * (`addNotificationResponseReceivedListener`) and the cold-start tap that
 * launched the app (`getLastNotificationResponseAsync`). `getIsAuthenticated`
 * is read at callback time so the listener always sees the current auth state.
 * Returns a cleanup function.
 */
export const registerNotificationListeners = (
  getIsAuthenticated: () => boolean
): (() => void) => {
  Notifications.getLastNotificationResponseAsync()
    .then((response) => {
      if (response) {
        dispatchNotificationRoute(
          response.notification.request.content.data,
          getIsAuthenticated()
        );
      }
    })
    .catch(() => {
      // Best-effort — a missing cold-start response is not an error.
    });

  const subscription = Notifications.addNotificationResponseReceivedListener((response) => {
    dispatchNotificationRoute(response.notification.request.content.data, getIsAuthenticated());
  });

  return () => subscription.remove();
};
